import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsDemoAccount } from '../../accountSelector/useIsDemoAccount';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../common/views/LoadingView';
import { safeReturnPath } from '../../coverEditor/coverEditorPath';
import { musicVideoEditorPath } from '../../musicVideoEditor/musicVideoEditorPath';
import { catalogSongPath } from '../../smartLinks/catalog/song/page/catalogSongPath';
import {
  directedScreenOf,
  isDirectedStepReadOnly,
  parseDirectedStepParam,
  parseMadeStepParam,
  shownDirectedScreen,
  type DirectedWizardStep,
} from '../directedSteps';
import { DirectedVideoContext, type DirectedVideoContextValue } from '../DirectedVideoContext';
import { directedVideoPath } from '../directedVideoPath';
import { directedMessages as messages } from '../messages/directedMessages';
import { useDirectedVideo } from '../useDirectedVideo';
import { DirectedStepBody } from './DirectedStepBody';
import { DirectedTopBar } from './DirectedTopBar';
import { useDirectedSong } from './useDirectedSong';
import { useDirectedVideoEvents } from './useDirectedVideoEvents';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectedVideoScreenProps {
  accountId: number;
  catalogItemUuid: string;
  directedVideoUuid: string;
}

/**
 * The wizard of one directed video (design directed-video b to g): it reads the video and its song,
 * picks the step from the data (`directedScreenOf`), and lets `?step=` open an earlier step again.
 * The URL holds the video uuid and the step on view, so reload, Back and Forward work (owner rule).
 * A refusal of the read (403 `M24_LIMIT_FEATURE`: the flag went off) shows the ErrorView.
 */
const DirectedVideoScreenComponent: React.FC<DirectedVideoScreenProps> = ({
  accountId,
  catalogItemUuid,
  directedVideoUuid,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const readOnly = useIsDemoAccount();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: directedVideoUuid }),
    [accountId, catalogItemUuid, directedVideoUuid]
  );
  const { video, error, refetch } = useDirectedVideo(args);
  const songRead = useDirectedSong(accountId, catalogItemUuid);
  const from = params.get('from');
  const requestedStep = parseDirectedStepParam(params.get('step'));
  const wantsMade = params.get('view') === 'made';
  const madeStep = parseMadeStepParam(params.get('step'));

  const leave = useCallback(() => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(safeReturnPath(from) ?? catalogSongPath(catalogItemUuid, { section: 'details', accountId }));
  }, [accountId, catalogItemUuid, from, navigate]);
  const openStep = useCallback(
    (step: DirectedWizardStep | null) => {
      // A push: Back returns to the step before.
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(directedVideoPath(catalogItemUuid, directedVideoUuid, { accountId, from, step }));
    },
    [accountId, catalogItemUuid, directedVideoUuid, from, navigate]
  );
  const openMade = useCallback(
    (step: DirectedWizardStep | null) => {
      // A push: Back returns to the video.
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(directedVideoPath(catalogItemUuid, directedVideoUuid, { accountId, from, step, view: 'made' }));
    },
    [accountId, catalogItemUuid, directedVideoUuid, from, navigate]
  );
  const openEditor = useCallback(
    (cutUuid: string) => {
      const back = directedVideoPath(catalogItemUuid, directedVideoUuid, { accountId, from });
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(musicVideoEditorPath(catalogItemUuid, { accountId, cut: cutUuid, from: back }));
    },
    [accountId, catalogItemUuid, directedVideoUuid, from, navigate]
  );

  const { song } = songRead;
  const value = useMemo((): DirectedVideoContextValue | null => {
    if (video === undefined || song === undefined) {
      return null;
    }
    const currentScreen = directedScreenOf(video);
    // "How it was made" exists only after the make started; before it, `?view=made` is ignored.
    const isMadeView = wantsMade && currentScreen === 'progress';
    const screen = isMadeView ? currentScreen : shownDirectedScreen(currentScreen, requestedStep);
    return {
      accountId,
      catalogItemUuid,
      video,
      song,
      screen,
      currentScreen,
      // An earlier step opened after the make is read only (the server refuses every edit then).
      readOnly: readOnly || isDirectedStepReadOnly(currentScreen, screen),
      openStep,
      isMadeView,
      madeStep: isMadeView ? madeStep : null,
      openMade,
      openEditor,
      leave,
    };
  }, [
    accountId,
    catalogItemUuid,
    leave,
    madeStep,
    openEditor,
    openMade,
    openStep,
    readOnly,
    requestedStep,
    song,
    video,
    wantsMade,
  ]);

  useDirectedVideoEvents(value?.screen ?? null);

  const readError = video === undefined ? error : undefined;
  const songError = song === undefined ? songRead.error : undefined;
  if (readError !== undefined || songError !== undefined) {
    return (
      <div className={styles.fullState}>
        <ErrorView
          error={readError ?? songError}
          retry={() => {
            refetch().catch(console.error);
            songRead.refetch().catch(console.error);
          }}
        />
      </div>
    );
  }
  if (value === null) {
    return (
      <div className={styles.fullState} role="status">
        <LoadingView label={intl.formatMessage(messages.opening)} />
      </div>
    );
  }
  return (
    <DirectedVideoContext.Provider value={value}>
      <div className={styles.frame} data-testid="directed-video" data-screen={value.isMadeView ? 'made' : value.screen}>
        <DirectedTopBar />
        <DirectedStepBody />
      </div>
    </DirectedVideoContext.Provider>
  );
};

export const DirectedVideoScreen = TypedMemo(DirectedVideoScreenComponent);
